/**
 * threshold_tracker_controller.js — GST registration threshold tracker (Stimulus).
 *
 * Scoped to the tracker block on /gst/threshold-tracker. Projects when a
 * tradie's income will cross the $75,000 GST registration threshold, and
 * renders a progress meter, a month-by-month projection table and an alert
 * saying how long there is to prepare.
 *
 * It hydrates markup the template already rendered rather than building any of
 * it, so the ids it reads are a contract with templates/gst/threshold-tracker.html.
 * querySelector returning null is not an error anyone sees, so a renamed id
 * fails silently.
 *
 * Replaces an end-of-body inline <script> that bound listeners via getElementById
 * + addEventListener and initialised on DOMContentLoaded — neither survives a Turbo
 * <body> swap. connect() runs the initial projection on every navigation; inputs
 * are wired through data-action. Element IDs and field names are preserved.
 */

import { Controller } from "@hotwired/stimulus";

    var GST_THRESHOLD = 75000;
    var REGISTRATION_DEADLINE_DAYS = 21;

export default class extends Controller {
        connect() {
            this.updateTracker();
        }

        updateTracker() {
            var currentIncome = parseFloat(this._q('#currentIncome').value) || 0;
            var growthRate = parseFloat(this._q('input[name="growthRate"]:checked').value) / 100;

            var progressPercentage = Math.min((currentIncome / GST_THRESHOLD) * 100, 100);
            this._q('#progressPercentage').textContent = Math.round(progressPercentage) + '%';
            this._q('#thresholdFill').style.width = progressPercentage + '%';

            if (currentIncome >= GST_THRESHOLD) {
                this._q('#thresholdMonth').textContent = 'Already reached';
                this._q('#registrationDeadline').textContent = 'Now';
                this._updateUrgencyAlert('immediate', 'right now');
            } else {
                var monthsToThreshold = this._calculateMonthsToThreshold(currentIncome, growthRate);
                var thresholdDate = new Date();
                thresholdDate.setMonth(thresholdDate.getMonth() + Math.ceil(monthsToThreshold));

                var deadlineDate = new Date(thresholdDate);
                deadlineDate.setDate(deadlineDate.getDate() + REGISTRATION_DEADLINE_DAYS);

                this._q('#thresholdMonth').textContent = thresholdDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
                this._q('#registrationDeadline').textContent = deadlineDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

                var months = Math.ceil(monthsToThreshold);
                this._updateUrgencyAlert(
                    monthsToThreshold <= 3 ? 'high' : monthsToThreshold <= 6 ? 'medium' : 'low',
                    months + (months === 1 ? ' month' : ' months'));
            }

            this._updateProjectionTable(currentIncome, growthRate);
        }

        _calculateMonthsToThreshold(currentIncome, growthRate) {
            if (currentIncome >= GST_THRESHOLD) return 0;
            if (growthRate <= 0) return Infinity;

            var months = 0;
            var projectedIncome = currentIncome;
            var monthlyIncome = currentIncome / this._getCurrentMonthOfYear();

            while (projectedIncome < GST_THRESHOLD && months < 36) {
                months++;
                projectedIncome += monthlyIncome * (1 + growthRate * months);
            }

            return months;
        }

        _getCurrentMonthOfYear() {
            var now = new Date();
            var start = new Date(now.getFullYear(), 6, 1); // July 1st (start of Australian financial year)
            return Math.max(1, Math.min(12, ((now - start) / (1000 * 60 * 60 * 24 * 30)) + 1));
        }

        /**
         * Repaint the alert for one urgency level.
         *
         * timeToRegister is passed in rather than read back out of the DOM.
         * This method replaces the alert's innerHTML, which destroys the
         * #timeToRegister span living inside it, so reading that span here
         * worked exactly once: on the next keystroke the caller's
         * querySelector returned null and the whole update threw, leaving the
         * tool frozen on stale figures with nothing visibly wrong. Every
         * branch re-emits the span for the same reason.
         */
        _updateUrgencyAlert(level, timeToRegister) {
            var alertDiv = this._q('#urgencyAlert');
            var TONE = {
                immediate: 'alert-danger', high: 'alert-danger',
                medium: 'alert-warning', low: 'alert-info'
            };
            var LEAD = {
                immediate: 'Register now:', high: 'Act soon:',
                medium: 'Worth starting:', low: 'Room to plan:'
            };

            var sentence = level === 'immediate'
                ? 'You need to register <strong id="timeToRegister">' + timeToRegister + '</strong>. ' +
                  'The threshold is already behind you, so the 21 days are running.'
                : 'You have <strong id="timeToRegister">' + timeToRegister + '</strong> before GST ' +
                  'registration becomes compulsory. Getting your records in order beforehand is ' +
                  'what makes the first BAS a check rather than a scramble.';

            alertDiv.className = 'alert d-flex align-items-center ' + TONE[level];
            alertDiv.innerHTML =
                '<i class="bi ' +
                (level === 'low' ? 'bi-info-circle-fill' : 'bi-exclamation-triangle-fill') +
                '"></i>' +
                '<div><strong>' + LEAD[level] + '</strong> ' + sentence + '</div>';
        }

        _updateProjectionTable(currentIncome, growthRate) {
            var tbody = this._q('#projectionTableBody');
            tbody.innerHTML = '';

            var projectedIncome = currentIncome;
            var monthlyIncome = currentIncome / this._getCurrentMonthOfYear();
            var currentDate = new Date();

            for (var i = 0; i < 12; i++) {
                var projectionDate = new Date(currentDate);
                projectionDate.setMonth(projectionDate.getMonth() + i);

                var monthIncome = monthlyIncome * (1 + growthRate * i);
                projectedIncome += monthIncome;

                var row = tbody.insertRow();
                row.innerHTML =
                    '<td>' + projectionDate.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) + '</td>' +
                    '<td>$' + monthIncome.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '</td>' +
                    '<td class="fw-bold">$' + projectedIncome.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '</td>' +
                    '<td>' + (projectedIncome >= GST_THRESHOLD
                        ? '<span class="badge bg-danger">GST Required</span>'
                        : '<span class="badge bg-success">Below Threshold</span>') + '</td>';

                if (projectedIncome >= GST_THRESHOLD) break;
            }
        }

        _q(selector) { return this.element.querySelector(selector); }
}
